import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Save,
  GripVertical,
  Info,
  CalendarClock,
  RefreshCcw,
} from 'lucide-react';
import { ImageUploadButton } from '@low6-pickem/ui';
import { uploadImage } from '@/api/upload';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useGetTenantsQuery } from '@/api/tenants/getTenants';
import { useGetPickemsQuery } from '@/api/pickems/getPickems';
import { useGetSquaresQuery } from '@/api/squares/getSquares';
import { useGetTenantLobbyTemplatesQuery } from '@/api/lobbies/lobbyTemplates';
import {
  useGetLobbyQuery,
  useUpdateLobbyMutation,
  useSyncLobbyTemplateAssetsMutation,
  useGetTenantCustomGamesQuery,
  useAddLobbyGameMutation,
  useRemoveLobbyGameMutation,
  useSetLobbyGameFeaturedMutation,
  useSetLobbyGameVisibilityMutation,
  useReorderLobbyGamesMutation,
  type LobbyFeaturedAssetType,
  type LobbyGame,
  type LobbySectionDefinition,
  type LobbySectionLayoutPayload,
} from '@/api/lobbies/getLobbies';
import { LobbySectionsEditor } from '@/components/lobby/LobbySectionsEditor';
import { TzDateTimeInput } from '@/components/datetime/TzDateTimeInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageContainer } from '@/components/layout/PageContainer';
import { ContentCard } from '@/components/layout/ContentCard';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export const Route = createFileRoute('/dashboard/lobby/$tenantCode/$lobbyId')({
  component: LobbyEditorPage,
});

function sectionDefinitionsToPayload(
  sectionDefinitions: LobbySectionDefinition[] | undefined
): LobbySectionLayoutPayload[] {
  return [...(sectionDefinitions ?? [])]
    .sort((a, b) => a.display_order - b.display_order)
    .map(section => ({
      display_order: section.display_order,
      kind: section.kind,
      title: section.title,
      subtitle: section.subtitle,
      max_games: section.max_games,
      payload: section.payload ?? {},
      sources: (section.sources ?? []).map(source => ({
        sport: source.sport,
        game_type: source.game_type,
        tags: source.tags ?? [],
        display_order: source.display_order,
      })),
      rules: (section.rules ?? []).slice(0, 1).map(rule => ({
        rule_type: rule.rule_type,
        display_order: rule.display_order,
        payload: rule.payload ?? {},
      })),
      manual_pickem_ids: section.manual_pickem_ids ?? [],
    }));
}

function LobbyEditorPage() {
  const { tenantCode, lobbyId } = Route.useParams();
  const navigate = useNavigate();

  const { data: tenants = [] } = useGetTenantsQuery();
  const tenant = tenants.find((t: any) => t.code === tenantCode);

  const { data: lobbyData, isLoading } = useGetLobbyQuery(lobbyId);
  const lobby = (lobbyData as any)?.lobby;
  const games: LobbyGame[] = (lobbyData as any)?.games ?? [];
  const sectionDefinitions = (
    lobbyData as { section_definitions?: LobbySectionDefinition[] } | undefined
  )?.section_definitions;

  // Fetch ALL game types from pickems table (pickem, squares, daily_reveal)
  // by passing game_type param. lobby_games FK references pickems.id so we
  // need pickem IDs regardless of game type.
  const { data: pickemGames = [] } = useGetPickemsQuery({ game_type: 'pickem' } as any);
  const { data: squaresGames = [] } = useGetPickemsQuery({ game_type: 'squares' } as any);
  const { data: dailyRevealGames = [] } = useGetPickemsQuery({ game_type: 'daily_reveal' } as any);
  const { data: squaresContests = [] } = useGetSquaresQuery();
  const { data: customGames = [] } = useGetTenantCustomGamesQuery(tenant?.tenant_id);

  const squaresStartByPickemId = new Map<string, string>();
  for (const square of squaresContests as Array<{
    pickem_id?: string;
    start_time_utc?: string;
    scheduled_game_start_utc?: string;
  }>) {
    if (!square.pickem_id) continue;
    const start = square.scheduled_game_start_utc?.trim() || square.start_time_utc?.trim() || null;
    if (start) squaresStartByPickemId.set(square.pickem_id, start);
  }

  const baseTenantGames = [...pickemGames, ...squaresGames, ...dailyRevealGames]
    .filter((p: any) => p.tenant_id === tenant?.tenant_id)
    .map((p: any) => ({
      id: p.pickem_id,
      title: p.title_en || 'Untitled',
      sport: p.sport,
      game_type: p.game_type || 'pickem',
      lobby_tags: Array.isArray(p.lobby_tags) ? p.lobby_tags : [],
      status: p.status ?? null,
      start_utc: p.start_utc ?? p.start ?? squaresStartByPickemId.get(p.pickem_id) ?? null,
      display_order: p.display_order ?? 0,
    }));

  const allTenantGames = baseTenantGames.concat(
    customGames.map((g: any) => ({
      id: g.id,
      title: g.title_en || 'Untitled',
      sport: g.sport,
      game_type: g.game_type || 'custom',
      lobby_tags: Array.isArray(g.lobby_tags) ? g.lobby_tags : [],
      status: null,
      start_utc: g.start_utc ?? null,
      display_order: 0,
    }))
  );

  const availablePickems = baseTenantGames.filter(g => !games.some(lg => lg.pickem_id === g.id));

  const { data: lobbyTemplates = [] } = useGetTenantLobbyTemplatesQuery(tenant?.tenant_id);
  const updateMutation = useUpdateLobbyMutation(lobbyId, tenant?.tenant_id);
  const syncAssetsMutation = useSyncLobbyTemplateAssetsMutation(lobbyId, tenant?.tenant_id);
  const addGameMutation = useAddLobbyGameMutation(lobbyId);
  const removeGameMutation = useRemoveLobbyGameMutation(lobbyId);
  const setVisibilityMutation = useSetLobbyGameVisibilityMutation(lobbyId);
  const setFeaturedMutation = useSetLobbyGameFeaturedMutation(lobbyId);
  const reorderMutation = useReorderLobbyGamesMutation(lobbyId);

  const [orderedGames, setOrderedGames] = useState<LobbyGame[]>([]);
  const gamesKey = games
    .map(
      g =>
        `${g.pickem_id}:${g.display_order}:${g.is_visible ? 1 : 0}:${g.featured_large ? 1 : 0}:${g.featured_medium ? 1 : 0}:${g.featured_small ? 1 : 0}:${g.featured_list_item ? 1 : 0}:${g.featured_arcade_header ? 1 : 0}`
    )
    .join('|');
  useEffect(() => {
    setOrderedGames(games);
  }, [gamesKey]);

  const updateLocalGame = (pickemId: string, patch: Partial<LobbyGame>) => {
    setOrderedGames(prev =>
      prev.map(game => (game.pickem_id === pickemId ? { ...game, ...patch } : game))
    );
  };

  const featuredFieldByAsset: Record<
    LobbyFeaturedAssetType,
    | 'featured_large'
    | 'featured_medium'
    | 'featured_small'
    | 'featured_list_item'
    | 'featured_arcade_header'
  > = {
    large: 'featured_large',
    medium: 'featured_medium',
    small: 'featured_small',
    list_item: 'featured_list_item',
    arcade_header: 'featured_arcade_header',
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setOrderedGames(prev => {
      const oldIdx = prev.findIndex(g => g.pickem_id === active.id);
      const newIdx = prev.findIndex(g => g.pickem_id === over.id);
      const reordered = arrayMove(prev, oldIdx, newIdx);
      reorderMutation.mutate(reordered.map(g => g.pickem_id));
      return reordered;
    });
  };

  const [form, setForm] = useState({
    title: '',
    slug: '',
    description: '',
    header_title: '',
    header_subtitle: '',
    banner_image_url: '',
    is_default: false,
    is_active: true,
    source_template_id: '' as string | null,
  });
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleUtc, setScheduleUtc] = useState('');
  const [selectedPickemId, setSelectedPickemId] = useState<string>('');
  const [pendingSections, setPendingSections] = useState<LobbySectionLayoutPayload[]>([]);

  useEffect(() => {
    if (lobby) {
      setForm({
        title: lobby.title ?? '',
        slug: lobby.slug ?? '',
        description: lobby.description ?? '',
        header_title: lobby.header_title ?? '',
        header_subtitle: lobby.header_subtitle ?? '',
        banner_image_url: lobby.banner_image_url ?? '',
        is_default: !!lobby.is_default,
        is_active: !!lobby.is_active,
        source_template_id:
          (lobby as { source_template_id?: string | null }).source_template_id ?? null,
      });
      const sa = (lobby as { scheduled_active_at?: string | null }).scheduled_active_at;
      setScheduleEnabled(!!sa);
      setScheduleUtc(sa ?? '');
    }
  }, [lobby]);

  useEffect(() => {
    setPendingSections(sectionDefinitionsToPayload(sectionDefinitions));
  }, [sectionDefinitions]);

  const handleImageUpload = async (data: { path: string; filename: string; imageData: string }) => {
    const result = await uploadImage(data);
    toast.success('Header image uploaded');
    return { imageUrl: result.imageUrl };
  };

  const handleSyncTemplateAssets = async () => {
    if (!form.source_template_id) {
      toast.error('Select an asset settings template before syncing assets.');
      return;
    }
    try {
      const result = await syncAssetsMutation.mutateAsync({
        template_id: form.source_template_id,
      });
      setForm(f => ({ ...f, source_template_id: result.template_id }));
      toast.success(
        `Synced ${result.images_applied} image${result.images_applied === 1 ? '' : 's'} from "${result.template_title}" (${result.config_sets} config set${result.config_sets === 1 ? '' : 's'})`
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to sync template assets';
      toast.error(message);
    }
  };

  const handleSaveAll = async () => {
    if (scheduleEnabled && !scheduleUtc.trim()) {
      toast.error(
        'Choose a date and time for scheduled activation, or turn off “Schedule active”.'
      );
      return;
    }
    try {
      await updateMutation.mutateAsync({
        title: form.title.trim(),
        slug: form.slug.trim(),
        description: form.description.trim() || null,
        header_title: form.header_title.trim() || null,
        header_subtitle: form.header_subtitle.trim() || null,
        banner_image_url: form.banner_image_url.trim() || null,
        is_default: form.is_default,
        is_active: form.is_active,
        scheduled_active_at: scheduleEnabled && scheduleUtc.trim() ? scheduleUtc.trim() : null,
        source_template_id: form.source_template_id || null,
        sections: pendingSections,
      });
      toast.success('Saved lobby details and layout');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save lobby details and layout';
      toast.error(message || 'Failed to save lobby details and layout');
    }
  };

  const handleAddGame = async () => {
    if (!selectedPickemId) return;
    await addGameMutation.mutateAsync({ pickem_id: selectedPickemId, display_order: games.length });
    setSelectedPickemId('');
  };

  if (!tenant) {
    return (
      <PageContainer>
        <p className="text-red-500">Tenant not found: {tenantCode}</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Breadcrumbs
        overrides={{
          lobby: 'Lobby',
          [tenantCode]: tenant.title || tenantCode,
          [lobbyId]: lobby?.title || 'Lobby',
        }}
      />

      <div className="flex items-center mb-6">
        <h1 className="text-2xl font-semibold">
          {isLoading ? 'Loading…' : (lobby?.title ?? 'Lobby')}
        </h1>
        <Button
          className="ml-auto mr-2"
          onClick={handleSaveAll}
          disabled={updateMutation.isPending}
        >
          <Save className="mr-2 h-4 w-4" />
          {updateMutation.isPending ? 'Saving…' : 'Save all changes'}
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate({ to: '/dashboard/lobby/$tenantCode', params: { tenantCode } })}
        >
          Back to {tenant.title} lobbies
        </Button>
      </div>

      <ContentCard>
        <h2 className="font-semibold mb-4">Lobby Setup</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              id="title"
              aria-label="Title (internal)"
              placeholder="Title (Internal)"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            />
          </div>
          <div>
            <div className="relative">
              <span
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 select-none text-muted-foreground/35"
                aria-hidden
              >
                /
              </span>
              <Input
                id="slug"
                aria-label="URL slug"
                className="pl-7"
                placeholder="URL Slug"
                value={form.slug}
                onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="source-template">Asset settings template</Label>
            <p className="mb-2 text-xs text-muted-foreground">
              Link this lobby to a template, then use Sync assets to pull the latest images and
              config sets from Asset Settings onto this lobby and the public lobby view.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <select
                id="source-template"
                className="flex h-10 min-w-0 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.source_template_id ?? ''}
                onChange={e =>
                  setForm(f => ({
                    ...f,
                    source_template_id: e.target.value ? e.target.value : null,
                  }))
                }
              >
                <option value="">None (no template link)</option>
                {lobbyTemplates.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.title} ({t.slug})
                  </option>
                ))}
              </select>
              <Button
                type="button"
                variant="outline"
                className="shrink-0"
                disabled={!form.source_template_id || syncAssetsMutation.isPending}
                onClick={handleSyncTemplateAssets}
              >
                <RefreshCcw
                  className={`mr-2 h-4 w-4 ${syncAssetsMutation.isPending ? 'animate-spin' : ''}`}
                />
                {syncAssetsMutation.isPending ? 'Syncing…' : 'Sync assets'}
              </Button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))}
            />
            Default lobby for this tenant
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
            />
            Active
          </label>
        </div>
      </ContentCard>

      <div className="mt-6" />

      <ContentCard>
        <h2 className="font-semibold mb-4 flex items-center gap-2">
          <CalendarClock className="h-4 w-4" aria-hidden />
          Schedule active (optional)
        </h2>
        <label className="flex items-center gap-2 text-sm mb-3">
          <input
            type="checkbox"
            checked={scheduleEnabled}
            onChange={e => {
              const on = e.target.checked;
              setScheduleEnabled(on);
              if (!on) setScheduleUtc('');
            }}
          />
          Schedule when this lobby becomes live for this slug
        </label>
        {scheduleEnabled ? (
          <div className="max-w-md">
            <TzDateTimeInput
              label="Go live at"
              id="lobby-schedule-edit"
              value={scheduleUtc}
              onChange={v => setScheduleUtc(v)}
            />
          </div>
        ) : null}
        <p className="text-xs text-muted-foreground mt-3 max-w-2xl">
          When the time is reached, this lobby becomes active and every other lobby with the same
          slug for this tenant becomes inactive. You cannot save with scheduling turned on unless a
          date and time are set. Two versions cannot share the same scheduled instant.
        </p>
      </ContentCard>

      <div className="mt-6" />

      <ContentCard>
        <h2 className="font-semibold mb-4">Lobby header</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="header-title">Header title</Label>
              <Input
                id="header-title"
                value={form.header_title}
                onChange={e => setForm(f => ({ ...f, header_title: e.target.value }))}
                placeholder="Shown in the lobby header"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="header-subtitle">Header subtitle</Label>
              <Input
                id="header-subtitle"
                value={form.header_subtitle}
                onChange={e => setForm(f => ({ ...f, header_subtitle: e.target.value }))}
                placeholder="Optional subtitle under the header title"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="header-image">Header image</Label>
            <div className="flex items-center gap-2">
              <Input
                id="header-image"
                value={form.banner_image_url}
                onChange={e => setForm(f => ({ ...f, banner_image_url: e.target.value }))}
                placeholder="https://..."
                className="flex-1"
              />
              <ImageUploadButton
                onUpload={url => setForm(f => ({ ...f, banner_image_url: url }))}
                path={`lobbies/${lobbyId}`}
                filename="header"
                uploadFn={handleImageUpload}
                title="Upload header image"
              />
            </div>
            {form.banner_image_url ? (
              <div className="mt-2 h-24 w-full overflow-hidden rounded-md border bg-gray-50">
                <img
                  src={form.banner_image_url}
                  alt="Header image preview"
                  className="h-full w-full object-cover"
                  onError={e => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </ContentCard>

      <div className="mt-6" />

      <ContentCard>
        <LobbySectionsEditor
          tenantCode={tenantCode}
          sectionDefinitions={sectionDefinitions}
          tenantPickems={allTenantGames}
          onSectionsChange={setPendingSections}
          toolbarLeading={
            <>
              <h2 className="text-base font-semibold leading-none">Lobby layout (sections)</h2>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="ghost" size="icon" aria-label="Lobby section help">
                    <Info className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="max-w-xl">
                  <p className="text-sm text-muted-foreground">
                    Drag sections to set top-to-bottom order. Game rows pull contests from your
                    tenant that match each section’s sport, game type, tags, and dynamic rules
                    (manual pins do not require adding games to the legacy lobby list). Popular and
                    recently played use entry counts and optional sign-in.
                  </p>
                </PopoverContent>
              </Popover>
            </>
          }
        />
      </ContentCard>

      <div className="mt-6" />

      {(!sectionDefinitions || sectionDefinitions.length === 0) && (
        <ContentCard>
          <h2 className="font-semibold mb-4">Games in this lobby</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            When you use lobby layout sections below, contests are discovered from your tenant by
            section rules — you do not need to add each game here. This table stays available if you
            are not using sections yet.
          </p>

          <div className="flex gap-2 mb-4">
            <Select value={selectedPickemId} onValueChange={setSelectedPickemId}>
              <SelectTrigger className="w-[400px]">
                <SelectValue placeholder="Add a game from this tenant…" />
              </SelectTrigger>
              <SelectContent>
                {availablePickems.length === 0 ? (
                  <div className="p-2 text-sm text-gray-500">No more games available</div>
                ) : (
                  availablePickems.map(g => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.title} — {g.sport} ({g.game_type})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <Button
              onClick={handleAddGame}
              disabled={!selectedPickemId || addGameMutation.isPending}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add
            </Button>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedGames.map(g => g.pickem_id)}
              strategy={verticalListSortingStrategy}
            >
              <Table>
                <TableHeader className="[&_tr]:border-b-0">
                  <TableRow>
                    <TableHead className="bg-gray-100 dark:bg-gray-800 rounded-l-xl font-bold w-10" />
                    <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Title</TableHead>
                    <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Sport</TableHead>
                    <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">Type</TableHead>
                    <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                      Visibility
                    </TableHead>
                    <TableHead className="bg-gray-100 dark:bg-gray-800 font-bold">
                      Featured
                    </TableHead>
                    <TableHead className="text-right bg-gray-100 dark:bg-gray-800 rounded-r-xl font-bold">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 2 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 7 }).map((__, j) => (
                          <TableCell key={j}>
                            <Skeleton className="h-4 w-24" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : orderedGames.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        No games in this lobby yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    orderedGames.map(g => (
                      <SortableGameRow
                        key={g.pickem_id}
                        game={g}
                        onToggleVisibility={() => {
                          const nextVisible = !g.is_visible;
                          updateLocalGame(g.pickem_id, { is_visible: nextVisible });
                          setVisibilityMutation.mutate(
                            {
                              pickemId: g.pickem_id,
                              isVisible: nextVisible,
                            },
                            {
                              onError: () =>
                                updateLocalGame(g.pickem_id, { is_visible: !nextVisible }),
                            }
                          );
                        }}
                        onToggleFeatured={(assetType, isFeatured) => {
                          const field = featuredFieldByAsset[assetType];
                          updateLocalGame(g.pickem_id, {
                            [field]: isFeatured,
                          } as Partial<LobbyGame>);
                          setFeaturedMutation.mutate(
                            {
                              pickemId: g.pickem_id,
                              assetType,
                              isFeatured,
                            },
                            {
                              onError: () =>
                                updateLocalGame(g.pickem_id, {
                                  [field]: !isFeatured,
                                } as Partial<LobbyGame>),
                            }
                          );
                        }}
                        onRemove={() => removeGameMutation.mutateAsync(g.pickem_id)}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </SortableContext>
          </DndContext>
        </ContentCard>
      )}
    </PageContainer>
  );
}

function SortableGameRow({
  game,
  onToggleVisibility,
  onToggleFeatured,
  onRemove,
}: {
  game: LobbyGame;
  onToggleVisibility: () => void;
  onToggleFeatured: (assetType: LobbyFeaturedAssetType, isFeatured: boolean) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: game.pickem_id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} className="border-b transition-colors hover:bg-muted/50">
      <td className="p-4 w-10">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab text-gray-400 hover:text-gray-600"
        >
          <GripVertical className="h-5 w-5" />
        </div>
      </td>
      <td className="p-4 font-medium">{game.title_en || 'Untitled'}</td>
      <td className="p-4">{game.sport ?? '—'}</td>
      <td className="p-4">
        <Badge variant="outline">{game.game_type ?? '—'}</Badge>
      </td>
      <td className="p-4">
        {game.is_visible ? <Badge>Visible</Badge> : <Badge variant="secondary">Hidden</Badge>}
      </td>
      <td className="p-4">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <label className="inline-flex items-center gap-1">
            <input
              type="checkbox"
              checked={Boolean(game.featured_large)}
              onChange={e => onToggleFeatured('large', e.target.checked)}
            />
            Large
          </label>
          <label className="inline-flex items-center gap-1">
            <input
              type="checkbox"
              checked={Boolean(game.featured_medium)}
              onChange={e => onToggleFeatured('medium', e.target.checked)}
            />
            Medium
          </label>
          <label className="inline-flex items-center gap-1">
            <input
              type="checkbox"
              checked={Boolean(game.featured_small)}
              onChange={e => onToggleFeatured('small', e.target.checked)}
            />
            Small
          </label>
          <label className="inline-flex items-center gap-1">
            <input
              type="checkbox"
              checked={Boolean(game.featured_list_item)}
              onChange={e => onToggleFeatured('list_item', e.target.checked)}
            />
            List Item
          </label>
          <label className="inline-flex items-center gap-1 col-span-2">
            <input
              type="checkbox"
              checked={Boolean(game.featured_arcade_header)}
              onChange={e => onToggleFeatured('arcade_header', e.target.checked)}
            />
            Action Header
          </label>
        </div>
      </td>
      <td className="p-4 text-right">
        <Button variant="ghost" size="sm" onClick={onToggleVisibility}>
          {game.is_visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </Button>
        <Button variant="ghost" size="sm" onClick={onRemove}>
          <Trash2 className="h-4 w-4 text-red-600" />
        </Button>
      </td>
    </tr>
  );
}
